import {
  delimit,
  isUndefined,
  MAIN_PRODUCT_CATEGORIES,
  PRODUCT_ENUMS_NL,
  Solution,
  SOLUTIONS_NL,
} from '@energiebespaarders/constants';
import config from '~/config';
import { getPvProductsFromQuote } from '~/hooks/useFormatQuoteTitle';
import type {
  mySingleQuote_quoteById_items_product_PvPanel_files as gqlProductFile,
  mySingleQuote_quoteById,
  mySingleQuote_quoteById_items,
  mySingleQuote_quoteById_items_product_FloorInsulation,
  mySingleQuote_quoteById_items_product_HeatPump,
  mySingleQuote_quoteById_items_product_PvPanel,
} from '~/types/generated/mySingleQuote';
import { ProductCategory } from '~/types/graphql-global-types';
import { formatBrandName } from '~/utils/formatBrandName';
import { floorInsulationInfoAccordions } from './accordionTexts/floorInsulation';
import { heatingInfoAccordions } from './accordionTexts/heating';
import { heatPumpInfoAccordions } from './accordionTexts/heatPump';
import { homeBatteryInfoAccordions } from './accordionTexts/homeBattery';
import { hybridInfoAccordions } from './accordionTexts/hybrid';
import { insulationGlazingInfoAccordions } from './accordionTexts/insulationGlazing';
import { pvSystemInfoAccordions } from './accordionTexts/pvSystem';
import { roofInsulationInfoAccordions } from './accordionTexts/roofInsulation';
import { underfloorHeatingInfoAccordions } from './accordionTexts/underfloorHeating';
import { wallInsulationInfoAccordions } from './accordionTexts/wallInsulation';
import { photoLabels } from './intakePhotoLabels';
import type { ReactNode } from 'react';
import type { BrandName, ProductCategory as ProductCat } from '@energiebespaarders/constants';

export type QuoteInfoAccordion<T = string> = {
  key: T;
  title: string;
  paragraphs: ReactNode[];
  icon: ReactNode;
  hide?: boolean;
};

export const getInfoAccordionsBySolution = (
  solution: Solution,
  needsPtc: boolean,
  needsPrepayment: boolean,
  certification?: 'insula' | 'skg',
): QuoteInfoAccordion[] => {
  // TODO: Can probably be refactored to a more modular setup, lots of repetition in the accordions between solutions
  switch (solution) {
    case Solution.PvSystem:
      return pvSystemInfoAccordions(needsPtc, needsPrepayment);
    case Solution.WallInsulation:
      return wallInsulationInfoAccordions(needsPtc, certification, needsPrepayment);
    case Solution.FloorInsulation:
    case Solution.CrawlspaceInsulation:
      return floorInsulationInfoAccordions(needsPtc, certification, needsPrepayment);
    case Solution.InnerRoofInsulation:
    case Solution.AtticFloorInsulation:
      return roofInsulationInfoAccordions(needsPtc, certification, needsPrepayment);
    case Solution.WindowGlazing:
    case Solution.WindowFrames:
      return insulationGlazingInfoAccordions(needsPtc, needsPrepayment);
    case Solution.CentralHeatingBoiler:
      return heatingInfoAccordions(needsPtc, needsPrepayment);
    case Solution.ElectricHeatPump:
      return heatPumpInfoAccordions(needsPtc, false, needsPrepayment);
    case Solution.HybridHeatPump:
      return hybridInfoAccordions(needsPtc, needsPrepayment);
    case Solution.AirToAirHeatPump:
      return heatPumpInfoAccordions(needsPtc, true, needsPrepayment);
    case Solution.UnderfloorHeating:
      return underfloorHeatingInfoAccordions(needsPtc, needsPrepayment);
    case Solution.HomeBattery:
      return homeBatteryInfoAccordions(needsPtc, needsPrepayment);
    case Solution.Miscellaneous:
    case Solution.Sedum:
    case Solution.ChargingBox:
      return [];
    default: {
      // TS trick to require exhaustive switch
      // return solution satisfies never; <-- Neater trick, not supported in current Next version due to old Babel dependency
      const exhaustiveCheck: never = solution;
      throw new Error(`Unhandled solution: ${exhaustiveCheck}`);
    }
  }
};

export const filterImages = (file: gqlProductFile, fileTag?: string) => {
  // 'svg' and 'jxl' are not supported yet
  const extensions = ['jpg', 'jpeg', 'png', 'bmp', 'gif', 'webp'];
  if (!extensions.includes(file.extension)) return false;
  if (fileTag) return file.metadata.tags.includes(fileTag);
  return true;
};

export const getFileSrc = (file: gqlProductFile) =>
  `${config.urls.files}/${file.id}.${file.extension}`;

// TODO: Also use an identifiable tag for legplan photos once available
export const getPhotoLabel = (photo: gqlProductFile) => {
  if (['quote', 'pvSystem'].every(tag => photo.metadata.tags.includes(tag))) return 'Legplan';
  return (
    photoLabels.find(({ key }) => photo.metadata.tags.includes(key))?.label || photo.metadata.title
  );
};

export const getProductPhotos = (
  quoteItems: readonly mySingleQuote_quoteById_items[],
): gqlProductFile[] => {
  const productsToShow = quoteItems
    .filter(item => item.product.isPresentedOnQuote)
    .map(item => item.product);
  const photos: gqlProductFile[] = productsToShow.flatMap(
    product => product.files.filter(file => filterImages(file, 'photo')) || [],
  );
  return [...new Set(photos)];
};

export const getInlineSolution = (solution: Solution) => {
  const nounSolutions: Solution[] = [
    Solution.CentralHeatingBoiler,
    Solution.HybridHeatPump,
    Solution.ElectricHeatPump,
    Solution.AirToAirHeatPump,
    Solution.HomeBattery,
    Solution.Sedum,
    Solution.ChargingBox,
  ];
  const solutionNL = SOLUTIONS_NL[solution]
    .toLowerCase()
    .replace(' binnenzijde', '')
    .replace('sedum', 'sedumdak');

  const inlineSolution = `${nounSolutions.includes(solution) ? 'een ' : ''}${solutionNL}`;
  return inlineSolution;
};

const checkAllCaps = (str: string) => str === str.toUpperCase();

/** This method uses the product properties to construct a simple and effective quote title for customers,
 * preventing technical product DB lingo from being presented in headings.
 * @example a PV quote can be titled `{amount} {brand} {series} {pMax} {panelType} {panelColor}` -> `10 LG Ultra 420Wp Mono Zwart`
 * @example a heat pump quote can be titled `{brand} {series} {pumpType} {maxPower}` -> `Vaillant aroTHERM Split 11,2kW`
 * @default the product title is returned if no specific product properties are found
 */
export const constructQuoteTitle = (mainItem?: mySingleQuote_quoteById_items) => {
  if (!mainItem) return '';
  let mainProduct = mainItem.product;

  if (mainItem.product.category === ProductCategory.pvPanel) {
    mainProduct = mainItem.product as mySingleQuote_quoteById_items_product_PvPanel;
    const brand = mainProduct.brand?.name
      ? formatBrandName(mainProduct.brand.name as BrandName)
      : '';
    const pMax = mainProduct.pMax ? `${delimit(mainProduct.pMax, 0)}Wp` : '';
    const panelType = mainProduct.panelType ? PRODUCT_ENUMS_NL[mainProduct.panelType] : '';
    const backsheetColor = mainProduct.backsheetColor
      ? PRODUCT_ENUMS_NL[mainProduct.backsheetColor]
      : '';

    return [
      mainItem.amount,
      brand,
      mainProduct.series || '',
      pMax,
      panelType.toLowerCase(),
      backsheetColor.toLowerCase(),
    ]
      .filter(v => !isUndefined(v))
      .join(' ')
      .trim();
  }

  if (mainItem.product.category === ProductCategory.heatPump) {
    mainProduct = mainItem.product as mySingleQuote_quoteById_items_product_HeatPump;
    const brand = mainProduct.brand?.name
      ? formatBrandName(mainProduct.brand.name as BrandName)
      : '';
    const power = mainProduct.power ? `${delimit(mainProduct.power / 1000, 1)}kW` : '';

    return [brand, mainProduct.series || '', 'warmtepomp', power]
      .filter(v => !isUndefined(v))
      .join(' ')
      .trim();
  }

  if (
    mainItem.product.category === ProductCategory.floorInsulationMaterial ||
    mainItem.product.category === ProductCategory.wallInsulationMaterial ||
    mainItem.product.category === ProductCategory.roofInsulationMaterial
  ) {
    mainProduct = mainItem.product as mySingleQuote_quoteById_items_product_FloorInsulation;
    const brand = mainProduct.brand?.name
      ? formatBrandName(mainProduct.brand.name as BrandName)
      : '';
    const material = mainProduct.material ? PRODUCT_ENUMS_NL[mainProduct.material] || '' : '';
    const formattedMaterial = checkAllCaps(material) ? material : material.toLowerCase();
    const thickness = mainProduct.thickness
      ? ` (${delimit(mainProduct.thickness / 10, mainProduct.thickness % 10 === 0 ? 0 : 1)}cm)`
      : '';
    const inlineSolution = brand
      ? SOLUTIONS_NL[mainItem.product.solution].toLowerCase()
      : SOLUTIONS_NL[mainItem.product.solution];
    return `${brand} ${inlineSolution} met ${formattedMaterial}${thickness}`.trim();
  }

  return mainItem.product.title;
};

/** Returns the subtitle for a quote, i.e. what is included in the quote aside from the main product
 * @example a PV quote can be titled `inclusief omvormer, optimizers en installatie`
 * @example a heat pump quote will be `geheel systeem inclusief installatie`
 */
export const constructQuoteSubtitle = (quote: mySingleQuote_quoteById) => {
  const mainQuoteItem = quote.items.find(
    item =>
      item.product?.category &&
      MAIN_PRODUCT_CATEGORIES.includes(item.product.category as unknown as ProductCat),
  );
  if (!mainQuoteItem) return '';
  const mainProductTitle = mainQuoteItem.product.title;

  // If product title already includes 'inclusief', return that part of the title
  if (mainProductTitle.includes('inclusief')) {
    return `inclusief ${mainProductTitle.split('inclusief')[1]}`;
  }

  if (quote.solution === Solution.PvSystem) {
    const pvProducts = getPvProductsFromQuote(quote);
    const inverterBrand = formatBrandName(pvProducts.pvInverterProduct?.brand?.name as BrandName);
    const hasOptimizers = !!pvProducts.pvOptimizerItem;
    return (
      `inclusief ${inverterBrand} omvormer${hasOptimizers ? ', optimizers' : ''} ` +
      'en installatie'
    );
  }

  if (quote.solution === Solution.HybridHeatPump || quote.solution === Solution.ElectricHeatPump) {
    return 'geheel systeem inclusief installatie';
  }

  if (quote.items.some(item => item.product.category === ProductCategory.labor)) {
    return 'inclusief installatie';
  }

  return '';
};
